import { Outlet, createRootRoute, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAutoSignin } from 'react-oidc-context';

import Loader from '@/components/Loader';
import { UserRoleEnum, UserRoleProvider, isRoleGreaterOrEqual, useUserRole } from '@/providers/user-role-provider';

import { RouteComponent as NotFound } from './error/404';

const redirectUrl = import.meta.env.VITE_REDIRECT_V1_URL;

function UserRoleGate() {
  const { t } = useTranslation();
  const { userRole } = useUserRole();
  const location = useLocation();
  if (!userRole || (userRole && !isRoleGreaterOrEqual(userRole, UserRoleEnum.ADMIN))) {
    const parts = location.pathname.split('/').filter(Boolean);
    const slug = parts[0];
    window.location.replace(`${redirectUrl}${slug}/csr_plan`);
    return <Loader loadingText={`${t('messages.redirectingToV1')} ...`} />;
  }

  return <Outlet />;
}

export const Route = createRootRoute({
  component: () => {
    const { t } = useTranslation();
    const location = useLocation();

    const redirectMappings = [
      { v2: '/access-management', v1: '/users/list' },
      { v2: '/profile', v1: '/users/me' },
      //{ v2: '/actions', v1: '/overview' },
      { v2: '/dashboard', v1: '/dashboard' },
    ];

    const { isLoading, isAuthenticated, error } = useAutoSignin({
      signinMethod: 'signinRedirect',
    });

    if (isLoading) {
      return <Loader loadingText={`Authentification en cours ...`} />;
    }

    if (!isAuthenticated) {
      return <div>Unable to log in</div>;
    }

    if (error) {
      return <div>An error occurred</div>;
    }

    if (redirectUrl) {
      const parts = location.pathname.split('/').filter(Boolean);
      const slug = parts[0];
      const pathAfterSlug = `/${parts.slice(1).join('/')}`;

      for (const { v2, v1 } of redirectMappings) {
        if (pathAfterSlug === v2) {
          const target = `${redirectUrl}${slug}${v1}`;
          window.location.replace(target); // full redirect
          return <Loader loadingText={`${t('messages.redirectingToV1')} ...`} />;
        }
      }
    }

    return (
      <UserRoleProvider>
        <UserRoleGate />
      </UserRoleProvider>
    );
  },

  notFoundComponent: () => <NotFound />,
});
